import { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { AudioPlayerProvider } from '../../../context/AudioPlayerContext';
import { useAuth } from '../../../firebase/useAuth';
import { useMetadata } from '../../../contexts/MetadataProvider';
import { useItemListener } from '../../../hooks/useItemListener';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../../common/GlassyIcon';
import ModalHeader from './ModalHeader';
import ModalTabs from './ModalTabs';
import ModalFooter from './ModalFooter';
import ConfirmationModal from '../ConfirmationModal';
import ReportIssueModal from '../ReportIssueModal';
import { useModal } from '../../../context/ModalContext';
import { getWordDetails, addWord, deleteWord, skipWord, unskipWord } from '../../../firebase/words';
import { getSentenceDetails, addSentence, deleteSentence, skipSentence, unskipSentence } from '../../../firebase/sentences';
import { getSound } from '../../../firebase/sounds';
import DetailsModalSkeleton from './skeletons/DetailsModalSkeleton';
import { SkeletonTheme } from 'react-loading-skeleton';

const DetailsModal = ({ item, handleClose, onUpdate, style, onMouseDown, isTopmost = true, initialTab = 'details' }) => {
  const { userId, userLoading, subscriptionStatus } = useAuth();
  const { learnedWordIds, wordIdsLoading } = useMetadata();

  const [baseDetails, setBaseDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loadingAction, setLoadingAction] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [currentDetailsId, setCurrentDetailsId] = useState(null);

  const { showModal, hideModal } = useModal();
  
  const isWord = !!item.word;
  
  // Use custom hook for listening to user progress
  const { userProgress, loading: userProgressLoading } = useItemListener(
    userId, 
    item.id, 
    isWord ? 'words' : 'sentences'
  );

  // Store the details state when an action starts to prevent UI updates during loading
  const [frozenDetails, setFrozenDetails] = useState(null);

  // Merge base item data with real-time user progress
  const rawDetails = useMemo(() => {
    if (!baseDetails) return null;
    
    if (userProgress) {
      return {
        ...baseDetails,
        ...userProgress,
        isLearned: true
      };
    }
    
    return {
      ...baseDetails,
      isLearned: false,
      srs_stage: 0,
      skipped: false,
      correct: 0,
      wrong: 0
    };
  }, [baseDetails, userProgress]);

  // Use frozen details during loading to prevent UI updates
  const details = loadingAction && frozenDetails ? frozenDetails : rawDetails;

  const isFullyLoading = isLoading || wordIdsLoading || userProgressLoading;

  // --- Height Animation Setup ---
  const animatedContainerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('auto');
  // New state to track animation and hide the scrollbar
  const [isAnimating, setIsAnimating] = useState(false);

  useLayoutEffect(() => {
    if (!animatedContainerRef.current) return;

    const measuredHeight = animatedContainerRef.current.scrollHeight;

    if (isFullyLoading) {
      setContentHeight(`${measuredHeight}px`);
    } else {
      // Set animating to true to hide scrollbar
      setIsAnimating(true);
      setContentHeight(`${measuredHeight}px`);

      const timer = setTimeout(() => {
        setContentHeight('auto');
        // Animation is done, show scrollbar again if needed
        setIsAnimating(false);
      }, 300); // Must match CSS transition duration

      return () => clearTimeout(timer);
    }
  }, [isFullyLoading]);

  // Initialize voice when details change
  useEffect(() => {
    if (details?.id && details.id !== currentDetailsId) {
      setCurrentDetailsId(details.id);
      setAudioUrl(null); // Clear audio URL when item changes
      
      if (details.word && details.sound_male && details.sound_female) {
        // Set initial voice based on word's preference
        const initialVoice = details.preferred_voice === 'random' 
          ? (Math.random() < 0.5 ? 'male' : 'female') 
          : (details.preferred_voice || 'male');
        setSelectedVoice(initialVoice);
      } else {
        setSelectedVoice(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.id, details?.preferred_voice, currentDetailsId]);

  useEffect(() => {
    if (details && details.id === currentDetailsId) {
      const isWord = !!details.word;
      if (isWord && details.sound_male && details.sound_female && selectedVoice) {
        // For words with voice options, use the selected voice
        const soundFile = selectedVoice === 'male' ? details.sound_male : details.sound_female;
        getSound(soundFile, 'words')
          .then(url => {
            // Only set audio URL if we're still on the same item
            if (details.id === currentDetailsId) {
              setAudioUrl(url);
            }
          })
          .catch(console.error);
      } else if (details.sound) {
        // For sentences or words without voice options, use the regular sound
        getSound(details.sound, isWord ? 'words' : 'sentences')
          .then(url => {
            if (details.id === currentDetailsId) {
              setAudioUrl(url);
            }
          })
          .catch(console.error);
      } else {
        setAudioUrl(null);
      }
    }
  }, [details, selectedVoice, currentDetailsId]);

  const fetchBaseDetails = useCallback(
    async () => {
      if (!item?.id) return;
      setIsLoading(true);
      const fetchFn = isWord ? getWordDetails : getSentenceDetails;
      try {
        const full = await fetchFn(item.id, userId);
        setBaseDetails(full);
      } catch (err) {
        setError(`Failed to load ${isWord ? 'word' : 'sentence'} details.`);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [item, userId, isWord]
  );

  useEffect(() => {
    if (!userLoading) fetchBaseDetails();
    setActiveTab(initialTab);
  }, [item.id, userLoading, fetchBaseDetails, initialTab]);

  // Simplified action handler - triggers onUpdate callback for parent components
  const handleAction = async (actionType, firebaseFunc) => {
    if (!userId || loadingAction || !details) return;
    
    // Freeze the current details state before starting the action
    setFrozenDetails(details);
    
    setLoadingAction(actionType);
    try {
      // Pass isSubscribed for add and skip operations
      if (actionType === 'add' || actionType === 'skip') {
        await firebaseFunc(userId, details.id, subscriptionStatus.isSubscribed);
      } else {
        await firebaseFunc(userId, details.id);
      }
      
      // Notify parent component to refetch this item
      if (onUpdate) {
        onUpdate(details.id);
      }
    } catch (err) {
      console.error(`Action "${actionType}" failed:`, err);
    } finally {
      setLoadingAction(null);
      setFrozenDetails(null); // Clear frozen details after action completes
    }
  };


  const handleDeleteClick = () => {
    const type = details?.word ? 'word' : 'sentence';
    showModal(
      <ConfirmationModal
        type="delete"
        onClose={hideModal}
        onConfirm={async () => {
          hideModal(); // Close modal first
          await handleAction('delete', type === 'word' ? deleteWord : deleteSentence);
        }}
      />
    );
  };

  const handleSkipClick = () => {
    const type = details?.word ? 'word' : 'sentence';
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={async () => {
          hideModal(); // Close modal first
          await handleAction('skip', type === 'word' ? skipWord : skipSentence);
        }}
      />
    );
  };

  const handleReportClick = () => showModal(<ReportIssueModal item={details} onClose={hideModal} />);

  if (!item) return null;

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <div
        className="bg-background-secondary w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col border border-border border-t-4 transition-colors duration-300 ease-in-out"
        style={{
          ...style,
          borderTopColor: details?.isLearned && !details?.skipped ? `var(--color-srs-${details.srs_stage})` : 'transparent',
        }}
        onMouseDown={onMouseDown}
      >
        {/* Header (Chrome) - Stays fixed */}
        <div className="p-4 flex-shrink-0 flex justify-between items-center bg-primary/5 border-b border-border rounded-t-2xl">
          <div className="flex items-center">
            <GlassyIcon icon={DocumentTextIcon} size="sm" color="orange" className="mr-3" />
            <h2 className="text-xl font-bold text-content-primary">Details</h2>
          </div>
          <button onClick={handleClose} className="p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Conditionally hide overflow during animation */}
        <div className={`flex-grow min-h-0 ${isAnimating ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div ref={animatedContainerRef} className="transition-[height] duration-300 ease-in-out" style={{ height: contentHeight }}>
            {isFullyLoading ? (
              <DetailsModalSkeleton sensesCount={item?.senses?.length} />
            ) : error ? (
              <div className="flex-grow flex items-center justify-center text-red-500 p-8 text-center">{error}</div>
            ) : (
              details && (
                <AudioPlayerProvider audioSrc={audioUrl}>
                  <ModalHeader
                    item={details}
                    onItemClick={(newItem) => showModal(<DetailsModal item={newItem} handleClose={hideModal} onUpdate={onUpdate} />)}
                    isTopmost={isTopmost}
                    learnedWordIds={learnedWordIds}
                    onVoiceChange={setSelectedVoice}
                    currentVoice={selectedVoice}
                  />
                  <ModalTabs
                    item={details}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onItemClick={(newItem, refetchCallback) => {
                      const nestedOnUpdate = (itemId) => {
                        // Call the parent onUpdate
                        if (onUpdate) onUpdate(itemId);
                        // Call the tab refetch callback
                        if (refetchCallback) refetchCallback(itemId);
                      };
                      showModal(<DetailsModal item={newItem} handleClose={hideModal} onUpdate={nestedOnUpdate} />);
                    }}
                    userId={userId}
                  />
                </AudioPlayerProvider>
              )
            )}
          </div>
        </div>

        {/* Footer - Stays fixed */}
        {userId && !isLoading && details && (
          <div className="flex-shrink-0">
            <ModalFooter
              item={details}
              itemType={details.word ? 'word' : 'sentence'}
              isTopModal={isTopmost}
              loadingAction={loadingAction}
              subscriptionStatus={subscriptionStatus}
              onAdd={() => handleAction('add', details.word ? addWord : addSentence)}
              onDeleteClick={handleDeleteClick}
              onSkipClick={handleSkipClick}
              onUnskip={() => handleAction('unskip', details.word ? unskipWord : unskipSentence)}
              onReportClick={handleReportClick}
            />
          </div>
        )}
      </div>
    </SkeletonTheme>
  );
};

export default DetailsModal;
